import { RootState } from '@Core/Communication'
import { CookiesStorage } from '@Core/Communication/GlobalStore/CookiesStorage/CookiesStorage'
import { ID_TOKEN_EVENT } from '@Core/Hooks/useSession'
import { getAuth } from 'firebase/auth'
import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

const OPS_URL = import.meta.env.VITE_ODFORGE_OPS_URL || ''
const storage = new CookiesStorage()

// Always prefer a fresh Firebase ID token (the SDK refreshes it); fall back
// to the one stored at login.
const currentToken = async () =>
    (await getAuth().currentUser?.getIdToken()) ||
    String(storage.getItem('@ofc:accessToken') || '')

/**
 * Hosts the odforge operations panel. The panel has no login of its own: it
 * asks this window for the user's token via postMessage and uses it against
 * the odforge API, which checks the operations:* permissions. The ODHUB theme
 * is pushed through the same channel so the panel follows it.
 */
export const Operations = () => {
    const frame = useRef<HTMLIFrameElement>(null)
    const opsOrigin = OPS_URL ? new URL(OPS_URL).origin : ''
    const { theme } = useSelector((state: RootState) => state.appConfig)

    const sendTheme = useCallback(() => {
        if (!opsOrigin) return
        frame.current?.contentWindow?.postMessage(
            { type: 'odforge-ops:theme', theme },
            opsOrigin
        )
    }, [opsOrigin, theme])

    useEffect(sendTheme, [sendTheme])

    useEffect(() => {
        if (!opsOrigin) return
        const onMessage = async (event: MessageEvent) => {
            if (
                event.origin !== opsOrigin ||
                event.source !== frame.current?.contentWindow ||
                event.data?.type !== 'odforge-ops:token-request'
            )
                return
            sendTheme()
            const token = await currentToken().catch(() => '')
            if (!token) {
                console.warn('Painel operacional: nenhum token de sessão disponível')
                return
            }
            frame.current?.contentWindow?.postMessage(
                { type: 'odforge-ops:token', token },
                opsOrigin
            )
        }
        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [opsOrigin, sendTheme])

    // Push every refreshed token so the panel never works with an expired
    // one (the protocol lets the parent send a token at any time).
    useEffect(() => {
        if (!opsOrigin) return
        const onToken = (event: Event) => {
            const token = (event as CustomEvent<string>).detail
            if (!token) return
            frame.current?.contentWindow?.postMessage(
                { type: 'odforge-ops:token', token },
                opsOrigin
            )
        }
        window.addEventListener(ID_TOKEN_EVENT, onToken)
        return () => window.removeEventListener(ID_TOKEN_EVENT, onToken)
    }, [opsOrigin])

    if (!OPS_URL) return <p>VITE_ODFORGE_OPS_URL não configurado.</p>

    return (
        <iframe
            ref={frame}
            src={OPS_URL}
            onLoad={sendTheme}
            title="Painel operacional"
            style={{
                border: 0,
                // Ocupa toda a área de conteúdo, anulando o padding lateral
                // do container, para o painel parecer parte do ODHUB.
                width: 'calc(100% + 48px)',
                margin: '0 -24px',
                height: '100%',
                display: 'block',
            }}
        />
    )
}
